import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Input, message, Upload, UploadProps } from "antd";
import { Controller, useForm } from "react-hook-form";
import { getSessionData } from "../../../utils/storage";
import LabelRequired from "../../Common/LabelRequired";
import { forwardRef, useImperativeHandle, useState } from "react";
import txtIcon from "../../../images/file-txt-icon.png";
import failIcon from "../../../images/upload-fail-icon.png";
import successIcon from "../../../images/upload-success-icon.png";
import { recorbAppDownloadSchema } from "../../../validation/extension.schema";
import { api } from "../../../utils/api";
import { LicenseState } from "../../../enum/lisence";
import json5 from "json5";
interface RecorbBoxDownloadProps {
  serialNumber: string | null;
  fileName: string;
  setFileName: (value: string) => void;
  hardwareSignature: string;
  setHardwareSignature: (value: string) => void;
  setSerialNumber: (value: string) => void;
  textNoti: string;
  setTextNoti: (value: string) => void;
  setHwNumber: (value: string) => void;
  uploadStatus: boolean;
  setUploadStatus: (value: boolean) => void;
}

export interface RecorbBoxDownloadRef {
  validate: () => Promise<boolean>;
}

const RecorbBoxDownload = forwardRef<
  RecorbBoxDownloadRef,
  RecorbBoxDownloadProps
>(
  (
    {
      fileName,
      setFileName,
      serialNumber,
      setSerialNumber,
      hardwareSignature,
      setHardwareSignature,
      setHwNumber,
      textNoti,
      setTextNoti,
      uploadStatus,
      setUploadStatus,
    },
    ref,
  ) => {
    const sessionData = getSessionData();

    const {
      control,
      formState: { errors },
      trigger,
      setValue,
    } = useForm({
      resolver: yupResolver(recorbAppDownloadSchema),
      mode: "onChange",
      defaultValues: {
        hardwareSignature: hardwareSignature || "",
        serialNumber: serialNumber || "",
        agentName: sessionData.agentName || "Agent name",
        agencyContactPerson: sessionData.agentPersonInCharge || "Agency name",
        agencyEmail: sessionData.agencyEmail || "agencyemail@dipro.vn",
      },
    });

    useImperativeHandle(ref, () => ({
      validate: async () => {
        return await trigger();
      },
    }));

    const getHWSerial = async ({
      serialNumber,
    }: {
      serialNumber: string;
    }): Promise<{
      success: boolean;
      hwSerial?: string;
    }> => {
      const req = await api.post("/api/licenses/hwSerialNumber", {
        serialNumber,
      });
      return { success: req.data.success, hwSerial: req.data?.hwSerial };
    };

    const getLicenseState = async ({
      serialNumber,
    }: {
      serialNumber: string;
    }) =>
      await api.get(`/api/licenses/serialNumber?serialNumber=${serialNumber}`);

    const clearData = () => {
      setSerialNumber("");
      setHardwareSignature("");
      setValue("serialNumber", "");
      setValue("hardwareSignature", "");
    };

    const props: UploadProps = {
      accept: ".txt",
      showUploadList: false,
      beforeUpload: (file) => {
        const isLtSize = file.size / 1024 <= 2;
        if (!isLtSize) {
          message.error(`File must be smaller than ${2}KB`);
          return Upload.LIST_IGNORE;
        }
        const reader = new FileReader();
        setFileName(file.name);
        reader.onload = async (e) => {
          try {
            const text = e.target?.result as string;
            const parsed = json5.parse(text);
            if (!parsed?.hardwareSignature || !parsed?.cameraSerialNumber) {
              setTextNoti("The file could not be uploaded. Please try again.");
              message.error("Upload Fail");
              clearData();
            } else {
              const hwLicense = await getHWSerial({
                serialNumber: parsed?.cameraSerialNumber,
              });
              const isSerialValid = await getLicenseState({
                serialNumber: parsed?.cameraSerialNumber,
              });
              if (!hwLicense.success) {
                clearData();
                message.error("Upload Fail");
                setTextNoti(
                  "Camera serial number not found. Please check and try again.",
                );
                setUploadStatus(true);
                setHwNumber("");
                return;
              } else {
                setHwNumber(hwLicense.hwSerial || "");
              }
              if (isSerialValid.data.state === LicenseState.REQUESTING) {
                clearData();
                message.error("Upload Fail");
                setTextNoti(
                  "Purchase information is already registered. Please check again.",
                );
                setUploadStatus(true);
                return;
              }
              setSerialNumber(parsed?.cameraSerialNumber);
              setHardwareSignature(parsed?.hardwareSignature);
              setValue("serialNumber", parsed?.cameraSerialNumber);
              setValue("hardwareSignature", parsed?.hardwareSignature);
              setTextNoti("File uploaded successfully.");
              message.success("Upload Success");
            }
            setUploadStatus(true);
          } catch (error) {
            setTextNoti("The file could not be uploaded. Please try again.");
            clearData();
            setUploadStatus(true);
            message.error("Upload Fail");
          }
        };
        reader.readAsText(file);
        return false;
      },
    };
    return (
      <div className="p-6 bg-[#F4F6F9] border border-[#BCC2FF] rounded-2xl flex gap-6">
        <div className="flex flex-col gap-4 w-1/2">
          <div>
            <LabelRequired text="Agent name" />
            <Controller
              name="agentName"
              control={control}
              render={({ field }) => (
                <div>
                  <Input
                    {...field}
                    type="text"
                    disabled
                    className="w-full h-[46px]"
                    status={errors.agentName ? "error" : ""}
                  />
                  {errors.agentName && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.agentName.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <div>
            <LabelRequired text="Name of person in charge of the agency" />
            <Controller
              name="agencyContactPerson"
              control={control}
              render={({ field }) => (
                <div>
                  <Input
                    {...field}
                    type="text"
                    disabled
                    className="w-full h-[46px]"
                    status={errors.agencyContactPerson ? "error" : ""}
                  />
                  {errors.agencyContactPerson && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.agencyContactPerson.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <div>
            <LabelRequired text="Agency contact email address" />
            <Controller
              name="agencyEmail"
              control={control}
              render={({ field }) => (
                <div>
                  <Input
                    {...field}
                    type="text"
                    disabled
                    className="w-full h-[46px]"
                    status={errors.agencyEmail ? "error" : ""}
                  />
                  {errors.agencyEmail && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.agencyEmail.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
        </div>
        <div className="flex flex-col gap-[10px] items-end w-1/2">
          <Upload {...props}>
            <Button className="base-button w-[282px] h-[50px] !text-xl !font-medium">
              Plugin Purchase Info File
            </Button>
          </Upload>
          {(uploadStatus || serialNumber) && (
            <div className="flex gap-[10px] items-center">
              <span
                className={
                  serialNumber && hardwareSignature
                    ? "text-[#098906] text-sm font-normal"
                    : "text-[#FF5646] text-sm font-normal"
                }
              >
                {textNoti}
              </span>
              {textNoti && (
                <img
                  className="h-6"
                  src={
                    serialNumber && hardwareSignature ? successIcon : failIcon
                  }
                  alt="txtIcon"
                />
              )}
            </div>
          )}
          {serialNumber && hardwareSignature && (
            <div className="flex gap-[10px] items-center">
              <span className="text-[#2C3A4B] text-sm font-medium">
                {fileName}
              </span>
              <img className="h-6" src={txtIcon} alt="txtIcon" />
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default RecorbBoxDownload;
